import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { CustomColorProps } from '../WidgetSettingWindow/components/config-components.interfaces';
import { ChartProps } from './chart.interface';

import { getColorsArray } from '../../helpers/get-colors-arrays';
import ChartGenericWrapper from '../common/chart-generic-wrapper';
import { defaultLegendWrapperStyle, renderLegend } from '../common/custom-legend.component';
import { CustomXAxis } from '../common/custom-X-axis.component';
import { yAxisSettings } from '../common/y-axis-properties';
import CustomTooltip from '../CustomToolTip/CustomToolTip';
import { LegendPayloadOnClick } from './chart-events';
import { findCustomColor, includeLegend } from './generalFunctions/general-functions';
import { filterChartValueByLegend, getDomainValue } from './generalFunctions/get-domaincharts';

import { useXAxisTopSettings } from './hooks/use-x-axis-top-settings';
import useInvalidateChartResponse from './hooks/useInvalidateChartResponse';
import useStartEndDates from './hooks/useStartEndDates';

import { useChartRollingFRQuery } from '../../redux/config/chart-data';
import { WidgetConfigurationData } from '../../redux/config/widgetApi';
import { getWidgets } from '../../redux/slices/layout-tabs-slice';

import { handleMouseEnter, handleMouseLeave } from '../../helpers/tooltip-functions';

export default function OdinRollingFRChart({ widgetId, layoutProps, activateAnimation }: ChartProps) {
  const [legendProps, setLegendProps] = useState<Record<string, boolean>>({});
  const [configurationData, setConfigurationData] = useState<WidgetConfigurationData>();

  const { startDate, endDate } = useStartEndDates();
  const xAxisSettings = useXAxisTopSettings();
  const widgets = useSelector(getWidgets);

  const {
    data: widgetChartData,
    isLoading: isWidgetChartDataLoading,
    isFetching: isWidgetChartDataFetching,
    isError,
    error,
  } = useChartRollingFRQuery({
    widgetId,
    from: startDate,
    to: endDate,
  });

  const selectItem = (e: LegendPayloadOnClick) => {
    setLegendProps({
      ...legendProps,
      [e.dataKey || e.value]: !legendProps[e.dataKey || e.value],
    });
  };

  const rollingFrDomainValue = useMemo(() => {
    const filteredData = filterChartValueByLegend(widgetChartData, legendProps);
    if (filteredData) {
      return getDomainValue(filteredData);
    }
    return 0;
  }, [widgetChartData, legendProps]);

  const barName = useMemo(() => {
    return configurationData?.widgetData.rollingTRIFR.title ?? 'TRIFR';
  }, [configurationData]);

  const colors: CustomColorProps[] = useMemo(
    () => getColorsArray([barName], configurationData),
    [configurationData, barName]
  );

  const handleRefetch = useInvalidateChartResponse(widgetId);

  useEffect(() => {
    if (widgets) {
      setConfigurationData(widgets.find((widget) => +widget.widget.id === +widgetId)?.widget);
    }
  }, [widgets, widgetId]);

  return (
    <ChartGenericWrapper
      isError={isError}
      error={error}
      isFetching={isWidgetChartDataFetching}
      isLoading={isWidgetChartDataLoading}
      refetch={handleRefetch}
      widgetId={widgetId}
      widgetConfigurationData={configurationData && { ...configurationData, layoutProps }}
      widgetChartData={widgetChartData}
      renderNoDataByDefault={!isWidgetChartDataFetching && !widgetChartData?.length}
    >
      {configurationData && widgetChartData && widgetChartData.length > 0 && (
        <ResponsiveContainer height="99%">
          <BarChart
            data={widgetChartData} // need to potentially sort this data by time or alpha
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 30,
            }}
          >
            <Tooltip content={<CustomTooltip />} cursor={false} isAnimationActive={false} />

            <CartesianGrid vertical={false} horizontal />
            <XAxis {...xAxisSettings} interval={0} tick={CustomXAxis} />
            <YAxis {...yAxisSettings([0, rollingFrDomainValue], 'Values', -90, -40)} />
            {includeLegend(configurationData) && (
              <Legend formatter={renderLegend} wrapperStyle={defaultLegendWrapperStyle} onClick={selectItem} />
            )}
            <Bar
              name={barName}
              dataKey="value"
              barSize={80}
              fill={findCustomColor(barName, colors)}
              hide={legendProps.value}
              onMouseEnter={() => handleMouseEnter(barName)}
              onMouseLeave={handleMouseLeave}
              isAnimationActive={activateAnimation}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </ChartGenericWrapper>
  );
}
